import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { PartyPopper, Paperclip, X, FileText, Image as ImageIcon, File as FileIcon, Trophy, Sparkles, Building2 } from "lucide-react";
import IconAction from "@/components/app/IconAction";
import api, { fmtIDR } from "@/lib_app/api";
import { toast } from "sonner";
import NumberInput from "@/components/app/NumberInput";
import DateInput from "@/components/app/DateInput";

const ALLOWED = /\.(pdf|jpe?g|png|gif|webp|docx?|xlsx?|csv|txt)$/i;
const MAX_SIZE = 15 * 1024 * 1024;

export function fileIcon(mime = "", filename = "") {
  const m = mime.toLowerCase();
  if (m.includes("pdf")) return FileText;
  if (m.startsWith("image/")) return ImageIcon;
  return FileIcon;
}

export const humanSize = (b) => {
  if (!b) return "0 B";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

export default function DealModal({ prospect, onOpenChange, onDone }) {
  const [realization, setRealization] = useState("");
  const [dealDate, setDealDate] = useState(null);
  const [note, setNote] = useState("");
  const [contractNo, setContractNo] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [ppnType, setPpnType] = useState("ppn");
  const [deadline, setDeadline] = useState("");
  const [valueIncludesPpn, setValueIncludesPpn] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    if (prospect) {
      setRealization(String(prospect.realization || prospect.prospect_value || ""));
      setDealDate(new Date().toISOString().slice(0, 10));
      setNote("");
      setContractNo(prospect.contract_no || "");
      setFiles([]);
      setPpnType("ppn");
      setDeadline("");
      setValueIncludesPpn(false);
    }
  }, [prospect]);

  const addFiles = (list) => {
    const arr = Array.from(list || []);
    const valid = [];
    arr.forEach((f) => {
      if (!ALLOWED.test(f.name)) return toast.error(`${f.name}: ekstensi tidak diizinkan`);
      if (f.size > MAX_SIZE) return toast.error(`${f.name}: melebihi 15MB`);
      valid.push(f);
    });
    setFiles((prev) => [...prev, ...valid]);
  };

  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!Number(realization)) return toast.error("Nilai harus diisi");
    setBusy(true);
    try {
      await api.post(`/prospects/${prospect.id}/deal`, {
        realization: Number(realization),
        deal_date: dealDate,
        note,
        contract_no: contractNo,
        ppn_type: ppnType,
        ppn_rate: 0.11,
        deadline: deadline || null,
        value_includes_ppn: valueIncludesPpn && ppnType === "ppn",
      });
      // Upload attachments to prospect (visible in Deal card + aggregated into Client docs)
      if (files.length > 0) {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        await api.post(`/prospects/${prospect.id}/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      const baseNum = Number(realization);
      let finalReal;
      if (ppnType === "ppn") {
        finalReal = valueIncludesPpn ? baseNum : baseNum + Math.round(baseNum * 0.11);
      } else {
        finalReal = baseNum;
      }
      toast.success(`🎉 Deal ${prospect.customer} · ${fmtIDR(finalReal)}${files.length ? ` · ${files.length} lampiran` : ""}`);
      onDone?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    } finally {
      setBusy(false);
    }
  };

  const val = Number(realization);
  const isPpn = ppnType === "ppn";
  const base = isPpn && valueIncludesPpn ? Math.round(val / 1.11) : val;
  const ppnAmt = isPpn ? (valueIncludesPpn ? val - base : Math.round(val * 0.11)) : 0;
  const finalTotal = base + ppnAmt;

  return (
    <Dialog open={!!prospect} onOpenChange={(o) => !o && onOpenChange(false)}>
      <DialogContent
        className="max-w-4xl p-0 rounded-3xl overflow-hidden max-h-[92vh] border-0 shadow-[0_40px_80px_-10px_rgba(16,185,129,0.35)]"
        data-testid="deal-modal"
      >
        <div className="relative bg-gradient-to-br from-emerald-50 via-white to-emerald-50/40 max-h-[92vh] overflow-y-auto">
          {/* Decorative sparkles */}
          <div className="pointer-events-none absolute top-6 left-8 text-emerald-300/60"><Sparkles className="w-5 h-5" /></div>
          <div className="pointer-events-none absolute top-14 right-24 text-emerald-400/50"><Sparkles className="w-4 h-4" /></div>
          <div className="pointer-events-none absolute top-24 left-1/3 text-emerald-300/40"><Sparkles className="w-3 h-3" /></div>

          {/* Header strip */}
          <div className="relative px-8 py-6 bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500 text-white">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-inner">
                <Trophy className="w-6 h-6" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/80">Momen Kemenangan</div>
                <DialogTitle className="text-2xl font-bold tracking-tight leading-tight">Tandai sebagai Deal</DialogTitle>
                <DialogDescription className="sr-only">Formulir konfirmasi deal untuk prospek</DialogDescription>
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="p-6 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
              {/* LEFT: Prospect summary card */}
              <aside className="md:col-span-5 space-y-4">
                <div className="rounded-2xl bg-white shadow-[0_8px_30px_-8px_rgba(16,185,129,0.25)] border border-emerald-100/60 p-5 space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                      <Building2 className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] uppercase tracking-widest text-emerald-700/70 font-bold">Klien</div>
                      <div className="text-lg font-bold text-[#090808] leading-tight truncate">{prospect?.customer}</div>
                      <div className="text-xs text-[#626061] mt-0.5 truncate">{prospect?.service}</div>
                    </div>
                  </div>
                  <div className="rounded-xl bg-gradient-to-br from-[#F4F5F7] to-emerald-50/40 p-3">
                    <div className="text-[10px] uppercase tracking-widest text-[#626061] font-bold mb-0.5">Pipeline Awal</div>
                    <div className="text-lg font-bold text-[#3B4A63] tabular-nums font-num">{fmtIDR(prospect?.prospect_value)}</div>
                  </div>
                  <div className="text-[11px] text-[#A0A3A8] leading-relaxed border-t border-emerald-100/60 pt-3">
                    Setelah ditandai deal, prospek ini otomatis <strong className="text-[#3B4A63]">menjadi Proyek</strong> baru yang bisa dipantau di menu Proyek.
                  </div>
                </div>

                {/* Realisasi hero card — updates realtime */}
                <div className={`rounded-2xl p-5 transition-all ${val > 0 ? "bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 text-white shadow-[0_16px_40px_-12px_rgba(16,185,129,0.5)]" : "bg-[#F4F5F7] text-[#A0A3A8]"}`} data-testid="deal-hero-card">
                  <div className="text-[10px] uppercase tracking-[0.24em] font-bold opacity-80 mb-1">Realisasi Akhir</div>
                  <div className="text-3xl font-bold font-num tabular-nums leading-tight">{val > 0 ? fmtIDR(finalTotal) : "Rp 0"}</div>
                  {val > 0 && (
                    <div className="text-xs opacity-90 mt-2 font-num tabular-nums">
                      {isPpn ? (
                        <>Dasar {fmtIDR(base)} + PPN {fmtIDR(ppnAmt)}</>
                      ) : (
                        <>Tanpa PPN — tercatat penuh sebagai Realisasi</>
                      )}
                    </div>
                  )}
                </div>
              </aside>

              {/* RIGHT: Form */}
              <div className="md:col-span-7 space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-widest text-[#3B4A63] font-bold">
                    {valueIncludesPpn && isPpn ? "Nilai Total (sudah termasuk PPN)" : "Nilai Dasar (Rp)"}
                  </Label>
                  <NumberInput
                    data-testid="deal-realization-input"
                    value={realization}
                    onChange={(v) => setRealization(v)}
                    className="text-2xl h-14 font-num font-bold text-[#090808] bg-white border-emerald-200 focus-visible:border-emerald-500 focus-visible:ring-emerald-500/30"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <button type="button" data-testid="deal-ppn-yes" onClick={() => setPpnType("ppn")} className={`h-11 rounded-xl text-sm font-semibold transition-all ${ppnType === "ppn" ? "bg-emerald-600 text-white shadow-md" : "bg-white text-[#3B4A63] border border-[#DCE0E7] hover:border-emerald-400"}`}>PPN 11%</button>
                  <button type="button" data-testid="deal-ppn-non" onClick={() => { setPpnType("non_ppn"); setValueIncludesPpn(false); }} className={`h-11 rounded-xl text-sm font-semibold transition-all ${ppnType === "non_ppn" ? "bg-[#090808] text-white shadow-md" : "bg-white text-[#3B4A63] border border-[#DCE0E7] hover:border-emerald-400"}`}>Non-PPN</button>
                </div>

                {ppnType === "ppn" && (
                  <label className="flex items-start gap-2 text-[11px] text-[#3B4A63] cursor-pointer select-none bg-emerald-50/60 rounded-xl p-2.5" data-testid="deal-value-includes-ppn-toggle">
                    <input
                      type="checkbox"
                      checked={valueIncludesPpn}
                      onChange={(e) => setValueIncludesPpn(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-emerald-300 accent-emerald-600"
                    />
                    <span>
                      Nilai di atas <strong>sudah termasuk PPN 11%</strong>
                      <span className="block text-[10px] text-[#A0A3A8]">Sistem hitung mundur untuk mendapat nilai dasar.</span>
                    </span>
                  </label>
                )}

                {/* Tanggal Deal + Deadline */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-widest text-[#3B4A63] font-bold">Tanggal Deal</Label>
                    <DateInput
                      data-testid="deal-date-input"
                      value={dealDate || ""}
                      onChange={(e) => setDealDate(e.target.value || null)}
                      className="bg-white border-[#DCE0E7]"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-widest text-[#3B4A63] font-bold">Deadline <span className="text-[#A0A3A8] font-normal normal-case">(opsional)</span></Label>
                    <DateInput
                      data-testid="deal-deadline-input"
                      value={deadline}
                      onChange={(e) => setDeadline(e.target.value)}
                      className="bg-white border-[#DCE0E7]"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-widest text-[#3B4A63] font-bold">Nomor Kontrak <span className="text-[#A0A3A8] font-normal normal-case">(opsional)</span></Label>
                  <Input
                    data-testid="deal-contract-input"
                    value={contractNo}
                    onChange={(e) => setContractNo(e.target.value)}
                    placeholder="Contoh: SPK-2026-08-014"
                    className="font-num bg-white border-[#DCE0E7]"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-widest text-[#3B4A63] font-bold">Catatan <span className="text-[#A0A3A8] font-normal normal-case">(opsional)</span></Label>
                  <Textarea
                    data-testid="deal-note-input"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={2}
                    placeholder="Keterangan bebas..."
                    className="bg-white border-[#DCE0E7]"
                  />
                </div>

                {/* Attachments */}
                <div className="space-y-2">
                  <Label className="text-[10px] uppercase tracking-widest text-[#3B4A63] font-bold">Lampiran <span className="text-[#A0A3A8] font-normal normal-case">(PO, Kontrak, PDF)</span></Label>
                  <input
                    ref={fileRef}
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt"
                    className="hidden"
                    onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                    data-testid="deal-file-input"
                  />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    data-testid="deal-add-files-btn"
                    className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-emerald-200 bg-emerald-50/40 rounded-xl py-3.5 text-sm text-[#3B4A63] hover:border-emerald-500 hover:bg-emerald-50/70 transition-colors font-semibold"
                  >
                    <Paperclip className="w-4 h-4" /> Klik untuk tambah file (max 15MB)
                  </button>
                  {files.length > 0 && (
                    <div className="space-y-1.5">
                      {files.map((f, i) => {
                        const Icon = fileIcon(f.type, f.name);
                        return (
                          <div key={i} className="flex items-center gap-2 bg-white border border-emerald-100 rounded-lg px-3 py-2 text-xs" data-testid={`deal-file-${i}`}>
                            <Icon className="w-4 h-4 text-emerald-600 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="truncate font-semibold text-[#090808]">{f.name}</div>
                              <div className="text-[#A0A3A8]">{humanSize(f.size)}</div>
                            </div>
                            <button type="button" onClick={() => removeFile(i)} className="w-6 h-6 rounded-md hover:bg-red-50 text-[#A0A3A8] hover:text-red-500 inline-flex items-center justify-center">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Sticky footer with prominent CTA */}
            <div className="sticky bottom-0 -mx-6 md:-mx-8 mt-6 px-6 md:px-8 py-4 bg-white/80 backdrop-blur-xl border-t border-emerald-100 flex items-center justify-between gap-3">
              <IconAction icon={X} label="Batal" onClick={() => onOpenChange(false)} testid="deal-cancel-btn" />
              <Button
                type="submit"
                disabled={busy || !val}
                data-testid="deal-submit-btn"
                className="h-12 px-8 rounded-full gap-2 bg-gradient-to-r from-emerald-500 via-emerald-600 to-teal-600 hover:from-emerald-600 hover:via-emerald-700 hover:to-teal-700 text-white font-bold shadow-[0_10px_30px_-8px_rgba(16,185,129,0.55)] disabled:opacity-50"
              >
                <PartyPopper className="w-5 h-5" /> {busy ? "Menyimpan..." : "Tandai Deal"}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
